import { useNavigate } from "react-router";

import {
  LearnAppRoutes,
  LoginAppRoutes,
  SjAdminAppRoutes,
  KaAppRoutes,
  AdminAppRoutes,
} from "../constants/routes";
import { checkUserLicenseStatus } from "../api";
import {
  retrieveTenantList,
  saveSelectedTenant,
  removeCurrentSelectedTenant,
  saveUsingCognitoCookies,
  saveAuthToken,
  cleanUserArtifacts,
  setIsSjAdminToStorage,
} from "../utils";
import { savePathForRedirect, getAndRemovePathForRedirect } from "../utils/redirectStorage";
import { retrieveAuthenticatedUserInfo } from "../utils/authService";

export const useLoginFlow = () => {
  let navigate;
  try {
    navigate = useNavigate();
  } catch (_e) {
    // Handle the case where useNavigate is not available
    navigate = (path: string) => {
      window.location.assign(path);
    };
  }

  const handleRedirectAfterLogin = async (isTenantAdmin: boolean = false) => {
    const storedPath = getAndRemovePathForRedirect() || "/";
    // Parse as a URL and keep only the path/query/hash, discarding any origin the
    // stored value may carry, so the redirect can never leave this app's origin
    const parsedPath = new URL(storedPath, window.location.origin);
    const redirectPath = parsedPath.pathname + parsedPath.search + parsedPath.hash;

    // If the user is a tenant admin and has no specific redirect destination, go to the admin portal
    if (isTenantAdmin && redirectPath === "/") {
      window.location.assign(AdminAppRoutes.route);
      return;
    }

    window.location.assign(redirectPath);
  };

  const handlePostLogin = async (token: string, sjAdmin: boolean, isDemo: boolean = false) => {
    saveAuthToken(token);
    saveUsingCognitoCookies();

    // If this is an sjAdmin login, save that to localStorage and then redirect to sj-admin dashboard
    if (sjAdmin) {
      setIsSjAdminToStorage();
      window.location.assign(SjAdminAppRoutes.route);

      return;
    }

    // If isDemo is true, skip tenant selection and license check, and go straight to demo
    if (isDemo) {
      window.location.href = KaAppRoutes.routes.demoLandingPage.route;
      return;
    }

    // Handle tenant selection flow
    const tenant = await initialTenantSelection();
    if (tenant) {
      // Only one tenant in auth token: select it
      const success = await doTenantSelectionAndRedirect(tenant);

      if (!success) {
        // License check failed, redirect to license error page
        navigate(LoginAppRoutes.routes.unlicensed.route);
      }
    } else {
      // Multiple tenants in auth token: go to tenant selection page
      navigate(LoginAppRoutes.routes.tenantSelection.route);
    }
  };

  // Helper function to handle tenant selection & redirect
  const doTenantSelectionAndRedirect = async (tenant: string): Promise<boolean> => {
    // 1. Check user license for the selected tenant
    const hasLicense = await checkUserLicense(tenant);
    if (!hasLicense) {
      return false; // License check failed, so don't store tenant or redirect
    }

    // 2. Save selected tenant
    saveSelectedTenant(tenant);

    // 3. Check if the user is a tenant admin for the selected tenant
    const isTenantAdmin = checkIsTenantAdmin(tenant);

    // 4. Redirect to appropriate post-login page
    handleRedirectAfterLogin(isTenantAdmin);
    return true;
  };

  const handleUnauthedAccess = () => {
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const domain = new URLSearchParams(currentSearch).get("domain");

    // User is trying to initiate SSO login
    if (currentPath === LearnAppRoutes.route && domain) {
      cleanUserArtifacts();
      window.location.assign(LoginAppRoutes.route + `?domain=${domain}`);
    } else {
      // Save the current path so we can redirect back after login
      savePathForRedirect(window.location.pathname + window.location.search);
      cleanUserArtifacts();
      window.location.assign(LoginAppRoutes.routes.signedOut.route);
    }
  };

  return {
    handlePostLogin,
    doTenantSelectionAndRedirect,
    handleRedirectAfterLogin,
    handleUnauthedAccess,
  };
};

// Helper function to handle initial tenant selection
const initialTenantSelection = async (): Promise<string | null> => {
  const tenantList = await retrieveTenantList();

  if (tenantList.length === 1) {
    return tenantList[0]!;
  } else {
    removeCurrentSelectedTenant();
    return null;
  }
};

// Helper function to check user license status in a specific tenant
const checkUserLicense = async (tenant_name: string): Promise<boolean> => {
  return await checkUserLicenseStatus(tenant_name);
};

// Helper function to check if the authenticated user has the tenant_admin role for a given tenant
const checkIsTenantAdmin = (tenant: string): boolean => {
  const userInfo = retrieveAuthenticatedUserInfo();
  if (!userInfo?.tenants) return false;

  const tenantInfo = userInfo.tenants.find((t: { name: string }) => t.name === tenant);
  return !!tenantInfo?.roles?.includes("tenant_admin");
};
